/**
 * Oversikt → Perioden (#273 v3.1): the month as Planlegg → Kontroller → Rapporter, from facts the API holds.
 * Nothing here is completed by visiting a page or closing a guide (OV3-02): each stage's state is read off
 * the shifts, the need, the pay month and the labour cost. A figure that is not known is null, never zero
 * (OV3-07, OV3-09). Nothing here writes.
 */
import type { ApiAbsence, ApiShift } from '@/api/data';
import type { CostDay } from '@/api/labour';
import { hours, oslo } from '@/rules';
import type { NeedProfile } from '@/shell/local';
import { addDays } from '@timefly/core';
import type { Kind } from '../fravaer/AbsenceRange';
import type { PayMonth } from '../lonn/Month';
import type { Counts } from '../timeliste/close';
import { lastDayOf, monthCounts } from '../timeliste/summary';
import { type HourGap, hourGaps } from '../vaktplan/staffing';
import { staffWork } from './cover';
import { shiftsOfDay } from './day';

const dateOf = (s: ApiShift) => oslo(s.start_time)?.date ?? '';
export const inMonth = (s: ApiShift, ym: string) => dateOf(s).slice(0, 7) === ym;

export interface PlanFacts {
  /** the days still ahead in the month that were checked, today included; 0 for a month that is over */
  days: number;
  /** a need is set for at least one weekday */
  hasNeed: boolean;
  /** each day ahead with a stretch under the need, with its first gap, for the link to that day */
  gapDays: { date: string; gaps: HourGap[] }[];
  gaps: number;
  /** open shifts and drafts from today to the month's end */
  open: number;
  drafts: number;
}

/**
 * The plan for the rest of the month, by the rule Dagen uses (published only, approved absence and planned
 * lunch out). Past days are not plan any more and are left to Kontroller. `shifts` must start the day
 * before the first day checked, for a night shift into it.
 */
export function planFacts(
  shifts: ApiShift[],
  ym: string,
  today: string,
  need: NeedProfile,
  absences: ApiAbsence[] | null,
  kindOf: (a: ApiAbsence) => Kind,
): PlanFacts {
  const first = `${ym}-01` > today ? `${ym}-01` : today;
  const last = lastDayOf(ym);
  const hasNeed = Object.values(need ?? {}).some((d) => Object.values(d ?? {}).some((n) => Number(n) > 0));
  const gapDays: PlanFacts['gapDays'] = [];
  let days = 0;
  for (let d = first; d <= last; d = addDays(d, 1)) {
    days++;
    if (!hasNeed) continue;
    const day = shiftsOfDay(shifts, d);
    const gaps = hourGaps(need, d, day, staffWork(day, absences, kindOf));
    if (gaps.length) gapDays.push({ date: d, gaps });
  }
  const ahead = shifts.filter((s) => inMonth(s, ym) && dateOf(s) >= today);
  return {
    days,
    hasNeed,
    gapDays,
    gaps: gapDays.reduce((n, g) => n + g.gaps.length, 0),
    open: ahead.filter((s) => s.user_id == null).length,
    drafts: ahead.filter((s) => !s.published).length,
  };
}

export interface ControlFacts extends Counts {
  /** approved hours in the month, the hours the pay report reads */
  approvedHours: number;
}
export function controlFacts(shifts: ApiShift[], ym: string, now: number, tol: number): ControlFacts {
  const mine = shifts.filter((s) => s.user_id != null && inMonth(s, ym));
  return {
    ...monthCounts(mine, ym, now, tol),
    approvedHours: mine.filter((s) => s.is_approved).reduce((h, s) => h + hours(s, null), 0),
  };
}

export type ReportState = 'waiting' | 'ready' | 'exported' | 'changed' | 'closed' | 'empty' | 'unknown';
/**
 * Rapporter's state for the month: never «ready» while Kontroller still has shifts to look at; an export
 * whose lines moved since is «changed» before it is «exported»; a closed month is closed.
 */
export function reportState(c: ControlFacts | null, pay: PayMonth | undefined): ReportState {
  if (!pay) return 'unknown';
  if (pay.state === 'closed') return pay.changed_since_export ? 'changed' : 'closed';
  if (pay.last_export) return pay.changed_since_export ? 'changed' : 'exported';
  if (!c) return 'unknown';
  if (c.action + c.ready > 0) return 'waiting';
  return c.approved > 0 ? 'ready' : 'empty';
}

/**
 * The month before, closed out or not (OV3-01): shifts not approved, approved hours never exported, or an
 * export whose lines changed since. A new empty month never hides it. null while its reads are missing.
 */
export function closeout(c: ControlFacts | null, pay: PayMonth | undefined, seesPay: boolean) {
  if (!c) return null;
  const left = c.action + c.ready;
  const unexported = seesPay && !!pay && !pay.last_export && pay.state !== 'closed' && c.approved > 0;
  const changed = seesPay && !!pay?.changed_since_export;
  return {
    left,
    unexported,
    changed,
    people: changed ? (pay?.changed_people ?? null) : null,
    done: !left && !unexported && !changed,
  };
}

export interface Economy {
  /** planned cost for the whole month */
  planned: number;
  /** clocked cost up to and including today */
  recorded: number;
  /** days that are over, and those of them with sales registered */
  pastDays: number;
  salesDays: number;
  sales: number | null;
  /** days with both sales and clocked hours: the only days a percentage is worked on */
  matchedDays: number;
  pct: number | null;
  /** shifts with no hourly rate, left out of the cost */
  unpriced: number;
}
/** Cost against sales for the month (OV3-07): sales are matched by date, a day with none is unknown, never 0 kr. */
export function economy(days: Map<string, CostDay>, ym: string, today: string): Economy {
  let planned = 0;
  let recorded = 0;
  let pastDays = 0;
  let salesDays = 0;
  let sales = 0;
  let matchedDays = 0;
  let mCost = 0;
  let mSales = 0;
  let unpriced = 0;
  for (let d = `${ym}-01`; d <= lastDayOf(ym); d = addDays(d, 1)) {
    const x = days.get(d);
    if (x) {
      planned += x.planned.cost;
      unpriced += x.unpriced;
    }
    if (d > today) continue;
    if (x) recorded += x.actual.cost;
    if (d === today) continue;
    pastDays++;
    if (x?.sales == null) continue;
    salesDays++;
    sales += x.sales;
    if (x.actual.shifts > 0 && x.sales > 0) {
      matchedDays++;
      mCost += x.actual.cost;
      mSales += x.sales;
    }
  }
  return {
    planned,
    recorded,
    pastDays,
    salesDays,
    sales: salesDays ? sales : null,
    matchedDays,
    pct: mSales > 0 ? (mCost / mSales) * 100 : null,
    unpriced,
  };
}
